import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Plus, Gauge, FileText, Pencil, Trash2 } from "lucide-react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import AppLayout from "@/components/AppLayout";
import PageHeader from "@/components/PageHeader";
import DataTable, { Column } from "@/components/DataTable";
import Drawer from "@/components/Drawer";
import { LoadingState } from "@/components/LoadingState";
import { leaseApi, meterApi, unitApi } from "@/api/endpoints";
import { useAuthStore } from "@/store/auth";
import { formatDate, formatDateTime, formatZar } from "@/utils/format";
import type { Lease, Meter, MeterStatus, Unit, UtilityType } from "@/types";

interface MeterForm {
  meter_number: string;
  utility_type: UtilityType;
  status: MeterStatus;
  alert_threshold?: number;
  description?: string;
}

interface LeaseForm {
  tenant_name: string;
  tenant_email?: string;
  tenant_phone?: string;
  lease_start_date: string;
  lease_end_date: string;
  rental_amount_zar?: number;
  deposit_zar?: number;
  breaker_size_amps?: number;
  connection_fee_zar?: number;
  fixed_utility_charge_zar?: number;
  notes?: string;
}

function expiryBadge(days?: number | null) {
  if (days == null) return null;
  if (days < 0) return <span className="badge bg-red-50 text-red-800">Expired</span>;
  if (days <= 14) return <span className="badge bg-red-50 text-red-800">{days}d left</span>;
  if (days <= 30) return <span className="badge bg-amber-50 text-amber-800">{days}d left</span>;
  if (days <= 60) return <span className="badge bg-yellow-50 text-yellow-800">{days}d left</span>;
  return <span className="badge bg-brand-50 text-brand-800">{days}d left</span>;
}

export default function UnitDetailPage() {
  const { orgId, buildingId, unitId } = useParams<{ orgId?: string; buildingId: string; unitId: string }>();
  const user = useAuthStore((s) => s.user)!;
  const canManage = user.is_staff || !!user.role?.can_manage_meters;
  const canManageLease = user.is_staff || !!user.role?.can_manage_units;
  const [unit, setUnit] = useState<Unit | null>(null);
  const [meters, setMeters] = useState<Meter[]>([]);
  const [leases, setLeases] = useState<Lease[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [leaseDrawerOpen, setLeaseDrawerOpen] = useState(false);
  const [editingLease, setEditingLease] = useState<Lease | null>(null);
  const form = useForm<MeterForm>({ defaultValues: { utility_type: "electricity", status: "active" } });
  const leaseForm = useForm<LeaseForm>();

  const buildingHref = orgId
    ? `/staff/organisations/${orgId}/buildings/${buildingId}`
    : `/buildings/${buildingId}`;

  const load = async () => {
    if (!buildingId || !unitId) return;
    setLoading(true);
    try {
      const [u, m, l] = await Promise.all([
        unitApi.get(buildingId, unitId),
        meterApi.list(unitId),
        leaseApi.listForUnit(unitId),
      ]);
      setUnit(u);
      setMeters(m);
      setLeases(l);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [buildingId, unitId]);

  const submit = form.handleSubmit(async (values) => {
    if (!unitId) return;
    try {
      await meterApi.create(unitId, values);
      toast.success("Meter created");
      setDrawerOpen(false);
      form.reset({ utility_type: "electricity", status: "active" });
      load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  });

  const openNewLease = () => {
    setEditingLease(null);
    leaseForm.reset({
      tenant_name: "",
      tenant_email: "",
      tenant_phone: "",
      lease_start_date: "",
      lease_end_date: "",
    });
    setLeaseDrawerOpen(true);
  };

  const openEditLease = (l: Lease) => {
    setEditingLease(l);
    leaseForm.reset({
      tenant_name: l.tenant_name,
      tenant_email: l.tenant_email ?? "",
      tenant_phone: l.tenant_phone ?? "",
      lease_start_date: l.lease_start_date,
      lease_end_date: l.lease_end_date,
      rental_amount_zar: l.rental_amount_zar ?? undefined,
      deposit_zar: l.deposit_zar ?? undefined,
      breaker_size_amps: l.breaker_size_amps ?? undefined,
      connection_fee_zar: l.connection_fee_zar ?? undefined,
      fixed_utility_charge_zar: l.fixed_utility_charge_zar ?? undefined,
      notes: l.notes ?? "",
    });
    setLeaseDrawerOpen(true);
  };

  const submitLease = leaseForm.handleSubmit(async (values) => {
    if (!unitId) return;
    // Strip empty-string optionals so they serialise as null/absent.
    const payload: Partial<Lease> = {
      ...values,
      tenant_email: values.tenant_email || undefined,
      tenant_phone: values.tenant_phone || undefined,
      notes: values.notes || undefined,
    };
    try {
      if (editingLease) {
        await leaseApi.update(editingLease.id, payload);
        toast.success("Lease updated");
      } else {
        await leaseApi.create(unitId, payload);
        toast.success("Lease created");
      }
      setLeaseDrawerOpen(false);
      load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  });

  const removeLease = async (l: Lease) => {
    if (!confirm(`Delete the lease for ${l.tenant_name}? This cannot be undone.`)) return;
    try {
      await leaseApi.delete(l.id);
      toast.success("Lease deleted");
      load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };

  const columns: Column<Meter>[] = [
    { key: "number", header: "Meter #", render: (m) => <span className="font-medium">{m.meter_number}</span> },
    {
      key: "utility", header: "Utility",
      render: (m) => <span className="badge bg-slate-100 text-slate-700">{m.utility_type}</span>,
    },
    {
      key: "status", header: "Status",
      render: (m) =>
        m.status === "active" ? <span className="badge bg-brand-50 text-brand-800">Active</span>
        : m.status === "faulty" ? <span className="badge bg-red-50 text-red-800">Faulty</span>
        : <span className="badge bg-slate-100 text-slate-600">Inactive</span>,
    },
    {
      key: "last", header: "Last reading",
      render: (m) => m.last_reading_date
        ? <span className="text-xs">{m.last_reading_value ?? "?"} on {formatDateTime(m.last_reading_date)}</span>
        : <span className="text-slate-400 text-xs">never</span>,
    },
    { key: "threshold", header: "Alert threshold", render: (m) => m.alert_threshold ?? "—" },
  ];

  const leaseColumns: Column<Lease>[] = [
    {
      key: "tenant", header: "Tenant",
      render: (l) => (
        <div>
          <span className="font-medium">{l.tenant_name}</span>
          {l.is_active && <span className="badge bg-brand-50 text-brand-800 ml-2">Current</span>}
          {(l.tenant_email || l.tenant_phone) && (
            <div className="text-xs text-slate-500">{[l.tenant_email, l.tenant_phone].filter(Boolean).join(" · ")}</div>
          )}
        </div>
      ),
    },
    {
      key: "term", header: "Term",
      render: (l) => <span className="text-xs">{formatDate(l.lease_start_date)} → {formatDate(l.lease_end_date)}</span>,
    },
    { key: "expiry", header: "Expiry", render: (l) => (l.is_active ? expiryBadge(l.days_to_expiry) : <span className="text-slate-400 text-xs">—</span>) },
    { key: "rent", header: "Rent", render: (l) => formatZar(l.rental_amount_zar) },
    { key: "deposit", header: "Deposit", render: (l) => formatZar(l.deposit_zar) },
    {
      key: "actions", header: "",
      render: (l) =>
        canManageLease ? (
          <div className="flex gap-2">
            <button type="button" className="text-slate-500 hover:text-brand-700" onClick={() => openEditLease(l)} title="Edit">
              <Pencil size={15} />
            </button>
            <button type="button" className="text-slate-500 hover:text-red-600" onClick={() => removeLease(l)} title="Delete">
              <Trash2 size={15} />
            </button>
          </div>
        ) : null,
    },
  ];

  if (loading || !unit) {
    return <AppLayout><LoadingState /></AppLayout>;
  }

  return (
    <AppLayout>
      <div className="mb-4">
        <Link to={buildingHref} className="text-sm text-brand-700 hover:underline">← Back to building</Link>
      </div>
      <PageHeader
        title={`Unit ${unit.unit_number}`}
        subtitle={unit.is_common_area ? `Common area · ${unit.common_area_type ?? ""}` : (unit.tenant_name ?? "Vacant")}
        actions={
          canManage && (
            <button onClick={() => setDrawerOpen(true)} className="btn-primary" type="button">
              <Plus size={16} /> Add meter
            </button>
          )
        }
      />

      {!unit.is_common_area && (
        <div className="card p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><FileText size={16} /> Leases &amp; tenant</h3>
            {canManageLease && (
              <button onClick={openNewLease} className="btn-secondary" type="button">
                <Plus size={14} /> New lease
              </button>
            )}
          </div>
          <DataTable columns={leaseColumns} rows={leases} emptyMessage="No leases recorded for this unit yet" />
        </div>
      )}

      <div className="card p-4 mb-6">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><Gauge size={16} /> Meters</h3>
        <DataTable columns={columns} rows={meters} emptyMessage="No meters on this unit yet" />
      </div>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Add meter"
        footer={
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" type="button" onClick={() => setDrawerOpen(false)}>Cancel</button>
            <button className="btn-primary" type="button" onClick={submit}>Create</button>
          </div>
        }
      >
        <form className="space-y-4" onSubmit={submit}>
          <div>
            <label className="label">Meter number</label>
            <input className="input" placeholder="e.g. MTR-0001" {...form.register("meter_number", { required: true })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Utility type</label>
              <select className="input" {...form.register("utility_type", { required: true })}>
                <option value="electricity">Electricity</option>
                <option value="water">Water</option>
                <option value="gas">Gas</option>
              </select>
            </div>
            <div>
              <label className="label">Status</label>
              <select className="input" {...form.register("status", { required: true })}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="faulty">Faulty</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label">Alert threshold (optional)</label>
            <input type="number" step="0.01" className="input" {...form.register("alert_threshold", { valueAsNumber: true })} />
          </div>
          <div>
            <label className="label">Description</label>
            <input className="input" {...form.register("description")} />
          </div>
        </form>
      </Drawer>

      <Drawer
        open={leaseDrawerOpen}
        onClose={() => setLeaseDrawerOpen(false)}
        title={editingLease ? "Edit lease" : "New lease"}
        footer={
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" type="button" onClick={() => setLeaseDrawerOpen(false)}>Cancel</button>
            <button className="btn-primary" type="button" onClick={submitLease}>{editingLease ? "Save" : "Create"}</button>
          </div>
        }
      >
        <form className="space-y-4" onSubmit={submitLease}>
          <div>
            <label className="label">Tenant name</label>
            <input className="input" placeholder="e.g. Acme Trading (Pty) Ltd" {...leaseForm.register("tenant_name", { required: true })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Tenant email</label>
              <input type="email" className="input" {...leaseForm.register("tenant_email")} />
            </div>
            <div>
              <label className="label">Tenant phone</label>
              <input className="input" {...leaseForm.register("tenant_phone")} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Lease start</label>
              <input type="date" className="input" {...leaseForm.register("lease_start_date", { required: true })} />
            </div>
            <div>
              <label className="label">Lease end</label>
              <input type="date" className="input" {...leaseForm.register("lease_end_date", { required: true })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Monthly rental (R)</label>
              <input type="number" step="0.01" className="input" {...leaseForm.register("rental_amount_zar", { valueAsNumber: true })} />
            </div>
            <div>
              <label className="label">Deposit (R)</label>
              <input type="number" step="0.01" className="input" {...leaseForm.register("deposit_zar", { valueAsNumber: true })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Breaker size (A)</label>
              <input type="number" className="input" {...leaseForm.register("breaker_size_amps", { valueAsNumber: true })} />
            </div>
            <div>
              <label className="label">Connection fee (R)</label>
              <input type="number" step="0.01" className="input" {...leaseForm.register("connection_fee_zar", { valueAsNumber: true })} />
            </div>
          </div>
          <div>
            <label className="label">Fixed monthly utility charge (R)</label>
            <input type="number" step="0.01" className="input" {...leaseForm.register("fixed_utility_charge_zar", { valueAsNumber: true })} />
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea className="input" rows={3} {...leaseForm.register("notes")} />
          </div>
        </form>
      </Drawer>
    </AppLayout>
  );
}
